// src/cmps/TextEditor/plugins/DragHandlePlugin.jsx
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import {
  $getNearestNodeFromDOMNode,
  $getNodeByKey,
  $getRoot,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  DRAGOVER_COMMAND,
  DROP_COMMAND,
} from 'lexical'
import { eventFiles } from '@lexical/rich-text'
import { mergeRegister } from '@lexical/utils'
import { useEffect, useRef, useState } from 'react'
import {
  useFloating,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
} from '@floating-ui/react'
import { GripVertical } from 'lucide-react'

const DRAG_DATA_FORMAT = 'application/x-lexical-drag-block'
const HANDLE_OFFSET = 28
const LINE_OPACITY = '0.5'

export function DragHandlePlugin({ anchorElem }) {
  const [editor] = useLexicalComposerContext()
  const targetLineRef = useRef(null)
  const [blockElem, setBlockElem] = useState(null)

  const { refs, floatingStyles, update } = useFloating({
    placement: 'left',
    middleware: [offset(8), flip(), shift()],
    whileElementsMounted: autoUpdate,
  })

  const getAnchor = () => anchorElem?.current ?? anchorElem

  const throttle = (callback, delay) => {
    let shouldWait = false
    return (...args) => {
      if (shouldWait) return
      callback(...args)
      shouldWait = true
      setTimeout(() => (shouldWait = false), delay)
    }
  }

  useEffect(() => {
    const handlePointerMove = throttle((e) => {
      const anchor = getAnchor()
      if (!anchor) return

      const blockKeys = editor
        .getEditorState()
        .read(() => $getRoot().getChildrenKeys())

      for (const key of blockKeys) {
        const el = editor.getElementByKey(key)
        if (!el) continue

        const { top, bottom } = el.getBoundingClientRect()
        if (e.clientY >= top && e.clientY <= bottom) {
          if (blockElem !== el) {
            setBlockElem(el)
            refs.setReference(el)
            update()
          }
          return
        }
      }
      setBlockElem(null)
    }, 60)

    document.addEventListener('pointermove', handlePointerMove)
    return () => {
      document.removeEventListener('pointermove', handlePointerMove)
    }
  }, [anchorElem, editor, blockElem, refs, update])

  const onDragStart = (e) => {
    if (!blockElem) return
    editor.update(() => {
      const node = $getNearestNodeFromDOMNode(blockElem)
      if (node) {
        e.dataTransfer.setData(DRAG_DATA_FORMAT, node.getKey())
        e.dataTransfer.setDragImage(blockElem, 0, 0)
      }
    })
  }

  useEffect(() => {
    const anchor = getAnchor()
    if (!anchor || !anchor.getBoundingClientRect) return

    const showLine = (block, mouseY) => {
      const line = targetLineRef.current
      if (!line) return
      const aRect = anchor.getBoundingClientRect()
      const { top, bottom } = block.getBoundingClientRect()
      const posY = mouseY > (top + bottom) / 2 ? bottom : top
      line.style.opacity = LINE_OPACITY
      line.style.transform = `translate(${HANDLE_OFFSET}px, ${
        posY - aRect.top - 1
      }px)`
      line.style.width = `${aRect.width - HANDLE_OFFSET * 2}px`
    }

    const hideLine = () => {
      const line = targetLineRef.current
      if (line) line.style.opacity = '0'
    }

    return mergeRegister(
      editor.registerCommand(
        DRAGOVER_COMMAND,
        (e) => {
          if (eventFiles(e)[0]) return false
          if (!blockElem) return false
          showLine(blockElem, e.pageY)
          e.preventDefault()
          return true
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        DROP_COMMAND,
        (e) => {
          const key = e.dataTransfer?.getData(DRAG_DATA_FORMAT)
          const dragged = $getNodeByKey(key)

          if (!dragged) return false

          const mouseY = e.clientY
          let closestBlock = null
          let minDistance = Infinity

          editor.getEditorState().read(() => {
            const blockKeys = $getRoot().getChildrenKeys()
            for (const key of blockKeys) {
              const el = editor.getElementByKey(key)
              if (!el) continue

              const rect = el.getBoundingClientRect()
              const centerY = (rect.top + rect.bottom) / 2
              const distance = Math.abs(mouseY - centerY)

              if (distance < minDistance) {
                minDistance = distance
                closestBlock = { key, el, rect, after: mouseY > centerY }
              }
            }
          })

          if (!closestBlock) {
            console.warn('[DROP] → לא נמצא יעד חוקי')
            return false
          }

          const targetNode = $getNodeByKey(closestBlock.key)

          if (!targetNode || targetNode === dragged) {
            console.warn('[DROP] → יעד שווה לגרור')
            return false
          }

          editor.update(() => {
            const parent = dragged.getParent()
            if (!parent) return

            if (closestBlock.after) {
              targetNode.insertAfter(dragged)
              console.log('[DROP] → inserted after', targetNode.getKey())
            } else {
              targetNode.insertBefore(dragged)
              console.log('[DROP] → inserted before', targetNode.getKey())
            }
          })

          hideLine()
          return true
        },
        COMMAND_PRIORITY_HIGH
      )
    )
  }, [anchorElem, editor, blockElem])

  if (!blockElem) return null

return (
  <>
    <div
      ref={refs.setFloating}
      style={{
        ...floatingStyles,
        position: 'absolute',
        zIndex: 30, // מספיק גבוה אבל לא עובר UI
      }}
      className="opacity-100 cursor-grab"
      draggable
      onDragStart={onDragStart}
    >
      <GripVertical size={16} />
    </div>

    <div
      ref={targetLineRef}
      className="absolute pointer-events-none h-[2px] bg-blue-500 transition-opacity duration-200 opacity-0"
    />
  </>
)

}
